<script setup>

const route = useRoute()
const { locale } = useI18n()
const url = useRequestURL()
const colorMode = useColorMode()
const isLoading = ref(false)

const content = ref(null)

const { data: artist } = await useAPIFetch(`Artist/${route.params.slug}`)

if (!artist.value) {
  throw createError({
    statusCode: 404,
    statusMessage: 'Page Not Found',
		fatal: true
  })
}

const socials = artist.value.body.find(item => item.specific === 'Socials')
const albums = artist.value.body.find(item => item.specific === 'AlbumList')
const concerts = artist.value.body.find(item => item.specific === 'ConcertList')
const articles = artist.value.body.find(item => item.specific === 'ArticleList')
const videos = artist.value.body.find(item => item.specific === 'VideoList')
const merch = artist.value.body.find(item => item.specific === 'MerchList')

const albumItems = ref(albums?.items || null)
const albumCursor = ref(albums?.cursor || null)

const addToAlbums = async () => {
	isLoading.value = true

	const result = await useUIFetch(locale.value, `Artist/${route.params.slug}/component/${albums.id}`, {
		'cursor': albumCursor.value,
		'limit': 4,
	})

	albumItems.value = [...albumItems.value, ...result.items]

	albumCursor.value = result.cursor
	isLoading.value = false
}

const concertItems = ref(concerts?.items || null)
const concertCursor = ref(concerts?.cursor || null)

const addToConcerts = async () => {
	isLoading.value = true

	const result = await useUIFetch(locale.value, `Artist/${route.params.slug}/component/${concerts.id}`, {
		'cursor': concertCursor.value,
		'limit': 5,
	})

	concertItems.value = [...concertItems.value, ...result.items]

	concertCursor.value = result.cursor
	isLoading.value = false
}

const articleItems = ref(articles?.items || null)
const articleCursor = ref(articles?.cursor || null)

const addToArticles = async () => {
	isLoading.value = true

	const result = await useUIFetch(locale.value, `Artist/${route.params.slug}/component/${articles.id}`, {
		'cursor': articleCursor.value,
		'limit': 2,
	})

	articleItems.value = [...articleItems.value, ...result.items]

	articleCursor.value = result.cursor
	isLoading.value = false
}

const videoItems = ref(videos?.items || null)
const videoCursor = ref(videos?.cursor || null)

const addToVideos = async () => {
	isLoading.value = true

	const result = await useUIFetch(locale.value, `Artist/${route.params.slug}/component/${videos.id}`, {
		'cursor': videoCursor.value,
		'limit': 2,
	})

	videoItems.value = [...videoItems.value, ...result.items]

	videoCursor.value = result.cursor
	isLoading.value = false
}

const merchItems = ref(merch?.items || null)
const merchCursor = ref(merch?.cursor || null)

const addToMerch = async () => {
	isLoading.value = true

	const result = await useUIFetch(locale.value, `Artist/${route.params.slug}/component/${merch.id}`, {
		'cursor': merchCursor.value,
		'limit': 2,
	})

	merchItems.value = [...merchItems.value, ...result.items]

	merchCursor.value = result.cursor
	isLoading.value = false
}

const scrollToContent= () => {
  content?.value?.scrollIntoView({ behavior: 'smooth' })
}

useHeadData({
	title: artist.value.meta.title,
	description: artist.value.meta.description,
	author: artist.value.meta.author,
	copyright: artist.value.meta.copyright,
	keywords: artist.value.meta.keywords,
	og: artist.value.meta.og,
	url: url.href
})

</script>

<template>
	<div class="container">
		<div class="xl:grid grid-cols-12 gap-8">
			<div class="col-span-6">
				<ClientOnly>
					<h1 class="text-red mb-8 xl:mb-12">{{ artist.attrs.title }}</h1>
					<p class="text-lg xl:text-2xl leading-[130%] dark:text-light text-dark tracking-[-0.36px] mb-4 xl:mb-6 fvs600" v-html="artist.attrs.bio"></p>
				</ClientOnly>

				<div v-if="socials" class="mb-4 xl:mb-6 -ml-4">
					<Socials :items="socials.items" />
				</div>
				<div class="hidden xl:block">
					<svg @click="scrollToContent" class="cursor-pointer hover:translate-y-1 transition-transform" xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none">
						<path d="M11 20L11 7.43866e-05" :stroke="colorMode.preference === 'dark' ? '#F5F5F5' : '#1D1D1B'" stroke-width="3"/>
						<path d="M19 12.5L11 20.5L3 12.5" :stroke="colorMode.preference === 'dark' ? '#F5F5F5' : '#1D1D1B'" stroke-width="3" stroke-linecap="square" stroke-linejoin="round"/>
					</svg>
				</div>
			</div>
			<div class="col-span-6">
				<div v-if="artist.attrs.image">
					<NuxtImg class="mb-12 w-full" :src="artist.attrs.image.url" :alt="artist.attrs.title" />
				</div>
			</div>
		</div>
		<div ref="content">
			<ClientOnly>
				<div v-for="component in artist.body" :key="component.id">

					<!-- contacts -->
					<div v-if="component.component === 'ContactList'" class="xl:grid grid-cols-12 gap-8 my-8 xl:my-32">
						<div class="col-span-4">
							<h2 class="text-red mb-6 xl:mb-0">{{ component.title }}</h2>
						</div>
						<div class="col-span-8">
							<div class="xl:flex justify-between">
								<div v-for="(contact, index) in component.items" :key="index" class="mb-5 xl:mb-0">
									<h4 class="dark:text-light text-dark mb-3">{{ contact.job }}</h4>
									<a class="dark:text-light text-dark hover:underline" :href="`mailto:${contact.person.attrs.email}`">{{ contact.person.attrs.email }}</a>
								</div>
							</div>
						</div>
					</div>

					<!-- list / video -->
					<div v-if="component.component === 'List' && component.type === 'Video' && component.items.length" class="xl:grid grid-cols-12 gap-8 my-8 xl:my-32">
						<div class="col-span-4">
							<h2 class="text-red mb-6 xl:mb-0">{{ component.title }}</h2>
						</div>
						<div class="col-span-8">
							<div class="block mb-12" v-for="video in videoItems" :key="video.id">
								<Video :video="video" />
							</div>
							<LoadMore v-if="videoCursor" smallGrid @click="addToVideos" :loading="isLoading" />
						</div>
					</div>

					<!-- concerts (bands in town) -->
					<div v-else-if="component.component === 'List' && component.type === 'Concert' && concertItems.length" class="xl:grid grid-cols-12 gap-8 my-8 xl:my-32">
						<div class="col-span-4">
							<h2 class="text-red mb-6 xl:mb-0">{{ component.title }}</h2>
						</div>
						<div class="col-span-8">
							<div v-for="concert in concertItems" :key="concert.id" class="col-span-1">
								<Concert :concert="concert" />
							</div>
							<LoadMore v-if="concertCursor" smallGrid @click="addToConcerts" :loading="isLoading" />
						</div>
					</div>

					<!-- biography -->
					<div v-else-if="component.component === 'Text'" class="xl:grid grid-cols-12 gap-8 my-8 xl:my-32">
						<div class="col-span-4">
							<h2 class="text-red mb-6 xl:mb-0">{{ component.title }}</h2>
						</div>
						<div class="col-span-8">
							<p class="text-base xl:text-lg leading-[140%] dark:text-light text-dark mb-6" v-html="component.content" />
						</div>
					</div>

					<!-- related news -->
					<div v-else-if="component.component === 'RelatedList' && component.type === 'Article'" class="xl:grid grid-cols-12 gap-8 my-8 xl:my-32">
						<div class="col-span-4">
							<h2 class="text-red mb-6 xl:mb-0">{{ $t('generic.related-news') }}</h2>
						</div>
						<div class="col-span-8">
							<div class="grid md:grid-cols-2 gap-4 md:gap-8">
								<div v-for="article in articleItems" :key="article.id" class="col-span-1">
									<CoverArticle :attrs="article.attrs" :id="article.id" :type="article.type.toLowerCase()" />
								</div>
							</div>
							<LoadMore v-if="articleCursor" smallGrid @click="addToArticles" :loading="isLoading" />
						</div>
					</div>

					<!-- albums -->
					<div v-else-if="component.component === 'List' && component.type === 'Album'" class="xl:grid grid-cols-12 gap-8 my-8 xl:my-32">
						<div class="col-span-4">
							<h2 class="text-red mb-6 xl:mb-0">{{ component.title }}</h2>
						</div>
						<div class="col-span-8">
							<div class="grid md:grid-cols-2 gap-4 md:gap-8">
								<div v-for="album in albumItems" :key="album.id" class="col-span-1">
									<CoverAlbum :attrs="album.attrs" :id="album.id" />
								</div>
							</div>
							<LoadMore v-if="albumCursor" smallGrid @click="addToAlbums" :loading="isLoading" />
						</div>
					</div>
					<!-- merch -->
					<div v-else-if="component.component === 'List' && component.specific === 'MerchList'" class="xl:grid grid-cols-12 gap-8 my-8 xl:my-32">
						<div class="col-span-4">
							<h2 class="text-red mb-6 xl:mb-0">{{ component.title }}</h2>
						</div>
						<div class="col-span-8">
							<div class="grid md:grid-cols-2 gap-4 md:gap-8">
								<div v-for="item in merchItems" :key="item.id" class="col-span-1">
									<CoverDefault :attrs="item.attrs" :id="item.id" :type="item.type" />
								</div>
							</div>
							<LoadMore v-if="merchCursor" smallGrid @click="addToMerch" :loading="isLoading" />
						</div>
					</div>
				</div>
			</ClientOnly>
		</div>

	</div>
</template>